import { mixed, object, setLocale, string } from 'yup'
import { es } from 'yup-locales'

import { ImpresoraProps } from '../interfaces/impresora.ts'
import { ApiImpresoraInputProps } from '../interfaces/impresora.ts'
import {
  esImpresoraRed,
  IMPRESORA_IP_PUERTO_REGEX,
  IMPRESORA_RED_COLUMNAS_MAX,
  IMPRESORA_RED_COLUMNAS_MIN,
  IMPRESORA_RED_ESPERA_MAX,
  IMPRESORA_RED_ESPERA_MIN,
  IMPRESORA_RED_NOMBRE_MAX,
  IMPRESORA_RED_PUERTO_DEFAULT,
  ImpresoraRedCodepage,
  ImpresoraRedPapel,
  leerConfigRed,
  normalizarDireccionRed,
  serializarConfigRed,
} from '../services/impresoraRed.ts'

setLocale(es)

// Se mantienen los nombres usados por otros componentes del módulo
export { esImpresoraRed, IMPRESORA_IP_PUERTO_REGEX }
export const IMPRESORA_PUERTO_DEFAULT = IMPRESORA_RED_PUERTO_DEFAULT

/**
 * Tipo de impresora (solo para el formulario, se deduce de atributo1):
 * - sistema: impresora de Windows de esta PC (USB / compartida). atributo1 = ID de hardware de isi-print
 *   (o el nombre del SO en registros antiguos). La selecciona cada PC; sus ajustes se hacen en isi-print.
 * - red: impresora de red de la sucursal. atributo1 = IP:puerto y atributo2 = ajustes de isi-print (JSON).
 *   Se registra una vez aquí y se entrega a isi-print de todas las cajas con `syncNetworkPrinters`.
 */
export type ImpresoraConexion = 'sistema' | 'red'

/** Valores del formulario de registro / actualización de impresoras */
export interface ImpresoraFormProps {
  conexion: ImpresoraConexion
  nombre: string
  descripcion: string
  atributo1: string
  /** Texto libre, solo impresoras del sistema (en las de red guarda los ajustes) */
  atributo2: string
  // Ajustes de isi-print, solo impresoras de red
  papel: ImpresoraRedPapel
  /** Vacío = según el papel (80 mm = 48, 58 mm = 32) */
  columnas: string
  /** Vacío = cp850 de isi-print */
  codepage: ImpresoraRedCodepage | ''
  /** Segundos de espera si otra caja está imprimiendo. Vacío = 12 */
  espera: string
}

export const IMPRESORA_FORM_DEFAULT = (): ImpresoraFormProps => ({
  conexion: 'sistema',
  nombre: '',
  descripcion: '',
  atributo1: '',
  atributo2: '',
  papel: 'rollo-80',
  columnas: '',
  codepage: '',
  espera: '',
})

/** Impresora del backend -> valores del formulario */
export const impresoraToForm = (impresora: ImpresoraProps): ImpresoraFormProps => {
  const red = esImpresoraRed(impresora.atributo1)
  const config = leerConfigRed(red ? impresora.atributo2 : null)
  return {
    conexion: red ? 'red' : 'sistema',
    nombre: impresora.nombre ?? '',
    descripcion: impresora.descripcion ?? '',
    atributo1: red ? normalizarDireccionRed(impresora.atributo1) : (impresora.atributo1 ?? ''),
    atributo2: red ? '' : (impresora.atributo2 ?? ''),
    papel: config.paper,
    columnas: config.chars_per_line?.toString() ?? '',
    codepage: config.codepage ?? '',
    espera: config.network_retry_seconds?.toString() ?? '',
  }
}

const entero = (v: string) => (v?.trim() ? Number(v) : undefined)

/** Valores del formulario -> input del backend */
export const impresoraFormToInput = (form: ImpresoraFormProps): ApiImpresoraInputProps => {
  if (form.conexion === 'red') {
    return {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion?.trim() ?? '',
      atributo1: normalizarDireccionRed(form.atributo1),
      atributo2: serializarConfigRed({
        paper: form.papel,
        chars_per_line: entero(form.columnas),
        codepage: form.codepage || undefined,
        network_retry_seconds: entero(form.espera),
      }),
    }
  }
  return {
    nombre: form.nombre.trim(),
    descripcion: form.descripcion?.trim() ?? '',
    atributo1: form.atributo1?.trim() ?? '',
    atributo2: form.atributo2?.trim() ?? '',
  }
}

/** Entero opcional dentro de un rango, como texto del formulario */
const enteroOpcional = (min: number, max: number) =>
  string()
    .trim()
    .default('')
    .test('rango', `Debe ser un número entero entre ${min} y ${max}`, (v) => {
      if (!v) return true
      const n = Number(v)
      return Number.isInteger(n) && n >= min && n <= max
    })

const esRed = (conexion: ImpresoraConexion) => conexion === 'red'

export const impresoraInputValidator = object({
  conexion: mixed<ImpresoraConexion>().oneOf(['sistema', 'red']).default('sistema'),
  nombre: string()
    .trim()
    .required('El nombre es requerido')
    .when('conexion', {
      is: esRed,
      then: (schema) => schema.max(IMPRESORA_RED_NOMBRE_MAX),
      otherwise: (schema) => schema.max(80),
    }),
  descripcion: string().trim().max(250).default(''),
  atributo1: string()
    .trim()
    .max(150)
    .default('')
    .when('conexion', {
      is: esRed,
      then: (schema) =>
        schema
          .required('La IP de la impresora es requerida')
          .matches(IMPRESORA_IP_PUERTO_REGEX, 'Formato inválido. Ej. 192.168.1.100:9100'),
    }),
  atributo2: string().trim().max(150).default(''),
  papel: mixed<ImpresoraRedPapel>().oneOf(['rollo-80', 'rollo-58']).default('rollo-80'),
  columnas: enteroOpcional(IMPRESORA_RED_COLUMNAS_MIN, IMPRESORA_RED_COLUMNAS_MAX),
  codepage: string().default(''),
  espera: enteroOpcional(IMPRESORA_RED_ESPERA_MIN, IMPRESORA_RED_ESPERA_MAX),
})
